import { z } from "zod";

export const IdParamSchema = z.object({ id: z.string().min(1) });

export const SHIPPING_ALLOCATION_METHODS = [
  "by_value",
  "by_quantity",
  "manual",
] as const;

export const INVENTORY_CARRIERS = ["hung_arun", "anusith", "other"] as const;

export const GOODS_RECEIPT_STATUSES = ["draft", "confirmed", "void"] as const;

export const GoodsReceiptLineInputSchema = z.object({
  variantId: z.string().min(1),
  quantity: z.coerce.number().int().min(1),
  unitPurchaseCost: z.coerce.number().min(0),
  /** ໃຊ້ສະເພາະ shipping_allocation_method = "manual" */
  shippingAllocated: z.coerce.number().min(0).optional(),
  productName: z.string().min(1),
  variantName: z.string().min(1),
});
export type GoodsReceiptLineInput = z.infer<typeof GoodsReceiptLineInputSchema>;

export const CreateGoodsReceiptSchema = z.object({
  receivedAt: z.string().min(1).optional(),
  carrier: z.enum(INVENTORY_CARRIERS).optional().nullable(),
  carrierNote: z.string().max(200).optional().nullable(),
  trackingNumber: z.string().max(120).optional().nullable(),
  shippingFeeTotal: z.coerce.number().min(0).optional(),
  otherFeesTotal: z.coerce.number().min(0).optional(),
  shippingAllocationMethod: z
    .enum(SHIPPING_ALLOCATION_METHODS)
    .optional()
    .default("by_value"),
  note: z.string().max(1000).optional().nullable(),
  lines: z.array(GoodsReceiptLineInputSchema).min(1),
});
export type CreateGoodsReceiptDTO = z.infer<typeof CreateGoodsReceiptSchema>;

export const UpdateGoodsReceiptSchema = CreateGoodsReceiptSchema.partial({
  lines: true,
});
export type UpdateGoodsReceiptDTO = z.infer<typeof UpdateGoodsReceiptSchema>;

/** Body ສຳລັບ preview ຕົ້ນທຸນ — ບໍ່ຕ້ອງມີ receipt id (ຄຳນວນຈາກ payload ໂດຍກົງ) */
export const PreviewLandedCostSchema = z.object({
  shippingFeeTotal: z.coerce.number().min(0).optional().default(0),
  otherFeesTotal: z.coerce.number().min(0).optional().default(0),
  shippingAllocationMethod: z
    .enum(SHIPPING_ALLOCATION_METHODS)
    .optional()
    .default("by_value"),
  lines: z
    .array(
      z.object({
        variantId: z.string().min(1),
        quantity: z.coerce.number().int().min(1),
        unitPurchaseCost: z.coerce.number().min(0),
        shippingAllocated: z.coerce.number().min(0).optional(),
        productName: z.string().optional(),
        variantName: z.string().optional(),
      }),
    )
    .min(1),
});
export type PreviewLandedCostDTO = z.infer<typeof PreviewLandedCostSchema>;

export const GoodsReceiptListQuerySchema = z.object({
  status: z.enum(GOODS_RECEIPT_STATUSES).optional(),
  carrier: z.enum(INVENTORY_CARRIERS).optional(),
  search: z.string().optional(),
});
export type GoodsReceiptListQueryDTO = z.infer<
  typeof GoodsReceiptListQuerySchema
>;

export const IMPORT_ORDER_STATUSES = [
  "draft",
  "ordered",
  "at_carrier",
  "partially_received",
  "received",
  "cancelled",
] as const;

export const IMPORT_SOURCE_APPS = [
  "taobao",
  "1688",
  "shopee_cn",
  "manual",
  "other",
] as const;

export const ImportOrderLineInputSchema = z.object({
  variantId: z.string().min(1),
  quantityOrdered: z.coerce.number().int().min(1),
  productName: z.string().min(1),
  variantName: z.string().min(1),
});
export type ImportOrderLineInput = z.infer<typeof ImportOrderLineInputSchema>;

export const CreateImportOrderSchema = z.object({
  sourceApp: z.enum(IMPORT_SOURCE_APPS).optional().default("manual"),
  externalRef: z.string().max(200).optional().nullable(),
  carrier: z.enum(INVENTORY_CARRIERS).optional().nullable(),
  carrierNote: z.string().max(200).optional().nullable(),
  trackingInbound: z.string().max(120).optional().nullable(),
  expectedAt: z.string().optional().nullable(),
  note: z.string().max(1000).optional().nullable(),
  status: z.enum(["draft", "ordered"]).optional().default("draft"),
  lines: z.array(ImportOrderLineInputSchema).min(1),
});
export type CreateImportOrderDTO = z.infer<typeof CreateImportOrderSchema>;

export const UpdateImportOrderSchema = z.object({
  sourceApp: z.enum(IMPORT_SOURCE_APPS).optional(),
  externalRef: z.string().max(200).optional().nullable(),
  carrier: z.enum(INVENTORY_CARRIERS).optional().nullable(),
  carrierNote: z.string().max(200).optional().nullable(),
  trackingInbound: z.string().max(120).optional().nullable(),
  expectedAt: z.string().optional().nullable(),
  note: z.string().max(1000).optional().nullable(),
  status: z.enum(IMPORT_ORDER_STATUSES).optional(),
  lines: z.array(ImportOrderLineInputSchema).min(1).optional(),
});
export type UpdateImportOrderDTO = z.infer<typeof UpdateImportOrderSchema>;

export const ImportOrderListQuerySchema = z.object({
  status: z.enum(IMPORT_ORDER_STATUSES).optional(),
  search: z.string().optional(),
});
export type ImportOrderListQueryDTO = z.infer<
  typeof ImportOrderListQuerySchema
>;

export const CreateReceiptFromImportLineSchema = z.object({
  variantId: z.string().min(1),
  quantity: z.coerce.number().int().positive(),
});

export const CreateReceiptFromImportSchema = z.object({
  lines: z.array(CreateReceiptFromImportLineSchema).min(1).optional(),
});
export type CreateReceiptFromImportDTO = z.infer<
  typeof CreateReceiptFromImportSchema
>;

export const ReorderHintsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).optional().default(20),
});
export type ReorderHintsQueryDTO = z.infer<typeof ReorderHintsQuerySchema>;

export const ReorderPlanQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  targetDaysOfCover: z.coerce.number().int().min(1).max(365).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  productId: z.string().optional(),
  variantId: z.string().optional(),
});
export type ReorderPlanQueryDTO = z.infer<typeof ReorderPlanQuerySchema>;

export const VariantLotsQuerySchema = z.object({
  includeDepleted: z.coerce.boolean().optional().default(false),
});
export type VariantLotsQueryDTO = z.infer<typeof VariantLotsQuerySchema>;

export const StockWithoutLotQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type StockWithoutLotQueryDTO = z.infer<typeof StockWithoutLotQuerySchema>;

export const BundleAssemblySchema = z.object({
  bundleVariantId: z.string().min(1),
  quantity: z.coerce.number().int().min(1),
  note: z.string().max(500).optional(),
});
export type BundleAssemblyDTO = z.infer<typeof BundleAssemblySchema>;

export const BundleAssemblyPreviewQuerySchema = z.object({
  bundleVariantId: z.string().min(1),
  quantity: z.coerce.number().int().min(1),
});
export type BundleAssemblyPreviewQueryDTO = z.infer<
  typeof BundleAssemblyPreviewQuerySchema
>;

/** Same fields as assembly — split pre-assembled bundle stock back to components */
export const BundleDisassemblySchema = BundleAssemblySchema;
export type BundleDisassemblyDTO = z.infer<typeof BundleDisassemblySchema>;

export const BundleVariantParamSchema = z.object({
  variantId: z.string().min(1),
});

/**
 * Phase 13 import order contracts — schemas ที่ชื่อไม่ชน legacy ด้านบน
 * สำหรับ Create/Update ใช้จาก `./import-order-contracts` โดยตรง
 */
export {
  IMPORT_ORDER_CURRENCIES,
  IMPORT_ORDER_SOURCES,
  IMPORT_ORDER_TRACKING_STATUSES,
  ImportOrderIdParamSchema,
  ImportOrderLineSchema,
  ImportOrderSourceSchema,
  ImportOrderStatusSchema,
  ImportOrderTrackingIdParamSchema,
  ImportOrderTrackingInputSchema,
  ImportOrderTrackingSchema,
  ImportOrderTrackingStatusSchema,
  LandedCostPreviewSchema,
  MarkImportOrderShortLineSchema,
  MarkImportOrderShortSchema,
  ReceiveImportOrderLineSchema,
  ReceiveImportOrderSchema,
  UpdateImportOrderTrackingSchema,
} from "./import-order-contracts";

export type {
  ImportOrderLineDetailDTO,
  ImportOrderLineInputDTO,
  ImportOrderSourceDTO,
  ImportOrderStatusDTO,
  ImportOrderTrackingDTO,
  ImportOrderTrackingInputDTO,
  ImportOrderTrackingStatusDTO,
  LandedCostPreviewInputDTO,
  MarkImportOrderShortDTO,
  ReceiveImportOrderDTO,
  UpdateImportOrderTrackingDTO,
} from "./import-order-contracts";
